import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { PeerMediaState } from '../room/store.js'
import type { QualityLevel } from '../room/connectionQuality.js'
import { ConnectionQualityGauge } from './ConnectionQualityGauge.js'
import styles from './VideoTile.module.css'

interface VideoTileProps {
  stream: MediaStream
  label: string
  mirrored?: boolean
  muted?: boolean
  mediaState?: PeerMediaState
  /** Connection-quality level for this participant (007). Gauge hidden when omitted. */
  quality?: QualityLevel
  /** When provided the tile becomes an activatable control (click + Enter/Space). */
  onSelect?: () => void
  /** Accessible label for the select action (used as aria-label on the button overlay). */
  selectLabel?: string
}

export function VideoTile({
  stream,
  label,
  mirrored = false,
  muted = false,
  mediaState,
  quality,
  onSelect,
  selectLabel,
}: VideoTileProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)

  const hasVideo = stream.getVideoTracks().length > 0
  const hasAudio = stream.getAudioTracks().length > 0
  const micOff = !hasAudio || mediaState?.mic === false
  // Show the no-video placeholder when there's no track OR the remote peer
  // has explicitly disabled their camera (even if a frozen track is still present).
  const showVideo = hasVideo && mediaState?.cam !== false
  const camOff = mediaState?.cam === false

  // Bind the stream whenever the <video> is (re)mounted, not just when the
  // stream changes. The element unmounts while showVideo is false (cam off);
  // when the camera comes back on it remounts as a fresh element with a null
  // srcObject. `stream` is unchanged across that toggle, so without showVideo
  // in the deps the effect would not re-run and the tile would stay black
  // until a full reload.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.srcObject = stream
    // Autoplay is unreliable cross-browser; explicit play() catches it.
    video.play().catch(() => {
      /* user gesture might be required; tile still mounts */
    })
    return () => {
      video.srcObject = null
    }
  }, [stream, showVideo])

  // Derive initials from the label for the no-video placeholder.
  const initials = label
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()

  return (
    <div
      data-testid="video-tile"
      className={`${styles.tile} ${mirrored ? styles.mirrored : ''} ${onSelect ? styles.selectable : ''}`}
    >
      {showVideo ? (
        <video ref={videoRef} autoPlay playsInline muted={muted} />
      ) : (
        <div
          data-testid="tile-no-video"
          className={styles.noVideo}
          aria-label={t('tile.noVideo')}
        >
          <span className={styles.initials}>{initials}</span>
        </div>
      )}

      {camOff && (
        <div
          data-testid="tile-cam-off"
          className={styles.camOff}
          aria-label={t('tile.camOff')}
        >
          {/* Camera-off badge */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        </div>
      )}

      {micOff && (
        <div
          data-testid="tile-mic-off"
          className={styles.micOff}
          aria-label={t('tile.micOff')}
        >
          {/* Inline SVG mic-off icon — no external dependency needed */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
            <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
      )}

      {quality !== undefined && (
        <ConnectionQualityGauge level={quality} className={styles.connectionQuality} />
      )}

      <div className={styles.tileLabel}>{label}</div>

      {/* Invisible full-tile button overlay for selectable tiles (FR-005, FR-011) */}
      {onSelect && (
        <button
          type="button"
          className={styles.selectOverlay}
          aria-label={selectLabel ?? label}
          onClick={onSelect}
        />
      )}
    </div>
  )
}
